import React, { useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import LinearProgress from "@mui/material/LinearProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import {
  Settings as SettingsIcon,
  Mail as MailIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from "@mui/icons-material";
import logoImage from "../assets/images/digitalTwinLogo.png";
import { MD3_Z_INDEX } from "./MaterialDesignConstants";

/**
 * Header component with user info and logout functionality
 * @param {Object} props - Component props
 * @param {Object} props.user - Current user object
 * @param {Function} props.onLogout - Logout handler function
 * @param {Function} props.onToggleSidebar - Sidebar toggle handler
 * @param {string} props.currentPage - Current page name to display
 * @param {Function} props.onSettingsClick - Settings click handler (optional)
 * @param {boolean} props.showProgress - Show loading progress bar (optional)
 */
export function Header({
  user,
  onLogout,
  onToggleSidebar,
  currentPage,
  onSettingsClick,
  showProgress = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [isFullscreen, setIsFullscreen] = useState(false);

  /** Matches Fullscreen API across browsers (Esc exits fullscreen without clicking our button). */
  const readFullscreenElement = () =>
    document.fullscreenElement ??
    document.webkitFullscreenElement ??
    document.mozFullScreenElement ??
    document.msFullscreenElement ??
    null;

  useEffect(() => {
    /*
     * Track only the dashboard (page root) fullscreen. A camera/player going
     * fullscreen on its own element must not flip this icon; when it sits on
     * top of the dashboard's fullscreen, keep the previous state.
     */
    const syncFullscreenState = () => {
      const el = readFullscreenElement();
      if (!el) setIsFullscreen(false);
      else if (el === document.documentElement) setIsFullscreen(true);
    };
    document.addEventListener("fullscreenchange", syncFullscreenState);
    document.addEventListener("webkitfullscreenchange", syncFullscreenState);
    document.addEventListener("mozfullscreenchange", syncFullscreenState);
    document.addEventListener("msfullscreenchange", syncFullscreenState);
    syncFullscreenState();
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreenState);
      document.removeEventListener(
        "webkitfullscreenchange",
        syncFullscreenState,
      );
      document.removeEventListener("mozfullscreenchange", syncFullscreenState);
      document.removeEventListener(
        "msfullscreenchange",
        syncFullscreenState,
      );
    };
  }, []);
  /**
   * Handle user menu open
   */
  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle user menu close
   */
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handle logout click
   */
  const handleLogout = () => {
    handleUserMenuClose();
    onLogout();
  };

  /**
   * Handle settings click
   */
  const handleSettingsClick = () => {
    if (onSettingsClick) {
      onSettingsClick();
    }
  };

  const toggleFullscreen = () => {
    const root = document.documentElement;
    if (readFullscreenElement() === root) {
      const exit =
        document.exitFullscreen?.bind(document) ??
        document.webkitExitFullscreen?.bind(document) ??
        document.mozCancelFullScreen?.bind(document) ??
        document.msExitFullscreen?.bind(document);
      void exit?.()?.catch(() => { });
    } else {
      const req =
        root.requestFullscreen?.bind(root) ??
        root.webkitRequestFullscreen?.bind(root) ??
        root.mozRequestFullScreen?.bind(root) ??
        root.msRequestFullscreen?.bind(root);
      void req?.()?.catch(() => { });
    }
    /* Icon state updates via fullscreenchange (covers Esc / F11 / browser UI). */
  };

  return (
    <AppBar
      position="sticky"
      elevation={1}
      sx={{
        bgcolor: "#212121",
        zIndex: MD3_Z_INDEX.APP_BAR,
        border: "1px solid #E0E0E0 !important !important",
        borderRadius: "20px",
        boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1) !important !important",
        transition: "box-shadow 0.3s ease !important !important",
        "&:hover": {
          boxShadow: "0 0 15px 0 rgba(0, 0, 0, 0.2) !important !important",
        },
      }}
      component="header"
      role="banner"
    >
      {/* Loading Progress Bar */}
      {showProgress && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            height: "3px",
          }}
        />
      )}

      <Toolbar
        sx={{
          minHeight: "61px !important",
          px: {
            xs: 2,
            sm: 3,
            md: 4,
          },
          gap: {
            xs: 1,
            sm: 2,
            md: 3,
          },
          justifyContent: "space-between",
        }}
      >
        {/* Page Title */}
        <Typography
          variant="h6"
          component="h1"
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontWeight: 400,
            fontSize: {
              xs: "18px",
              sm: "22px",
              md: "26px",
            },

            lineHeight: "45px",
            color: "white",
            whiteSpace: "nowrap",
            flexShrink: 0,
            display: {
              xs: "none",
              sm: "block",
            },
          }}
        >
          {(currentPage && currentPage.heading)}
        </Typography>

        {/* Center Section - Logo and Company Name */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: {
              xs: 1,
              sm: 1.5,
              md: 2,
            },
            flex: 1,
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={logoImage}
            alt="Client Logo"
            sx={{
              height: {
                xs: "30px",
                sm: "35px",
                md: "40px",
              },
              width: "auto",
            }}
          />
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 400,
              fontSize: {
                xs: "18px",
                sm: "22px",
                md: "26px",
              },
              lineHeight: "39px",
              color: "#ff8555",
              whiteSpace: "nowrap",
              display: {
                xs: "none",
                md: "block",
              },
            }}
          >
            Oomni Watch
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
          component="nav"
          aria-label="header actions"
        >
          {/* <Tooltip title="Settings" arrow>
            <IconButton 
              onClick={handleSettingsClick} 
              size="small" 
              aria-label="open settings" 
              sx={{
                width: 32,
                height: 32,
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                },
                '&:focus-visible': {
                  outline: '2px solid white',
                  outlineOffset: '2px'
                }
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Messages (3 unread)" arrow>
            <IconButton 
              size="small" 
              aria-label="messages, 3 unread" 
              sx={{
                width: 32,
                height: 32,
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                },
                '&:focus-visible': {
                  outline: '2px solid white',
                  outlineOffset: '2px'
                }
              }}
            >
              <Badge badgeContent={3} color="error">
                <MailIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title="Notifications (5 new)" arrow>
            <IconButton 
              size="small" 
              aria-label="notifications, 5 new" 
              sx={{
                width: 32,
                height: 32,
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                },
                '&:focus-visible': {
                  outline: '2px solid white',
                  outlineOffset: '2px'
                }
              }}
            >
              <Badge badgeContent={5} color="error">
                <NotificationsIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip> */}
          <Tooltip
            title={
              isFullscreen ? "Exit fullscreen (F11)" : "Enter fullscreen (F11)"
            }
            arrow
          >
            <IconButton
              size="small"
              aria-label={
                isFullscreen ? "Exit fullscreen" : "Enter fullscreen"
              }
              sx={{
                width: 32,
                height: 32,
                color: isFullscreen ? "#2932E5" : "white",
                "&:hover": {
                  bgcolor: isFullscreen
                    ? "#2932E5"
                    : "rgba(255, 255, 255, 0.1)",
                },
                "&:focus-visible": {
                  outline: "2px solid white",
                  outlineOffset: "2px",
                },
              }}
              onClick={toggleFullscreen}
            >
              {isFullscreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="User Profile" arrow>
            <IconButton
              onClick={handleUserMenuOpen}
              size="small"
              aria-label="user profile"
              sx={{
                width: 32,
                height: 32,
                p: 0,
                "&:focus-visible": {
                  outline: "2px solid white",
                  outlineOffset: "2px",
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "#616161",
                  "&:hover": {
                    bgcolor: "#757575",
                  },
                }}
                alt="User Profile"
              >
                <PersonIcon fontSize="small" />
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* User Menu Dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleUserMenuClose}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              elevation: 3,
              sx: {
                borderRadius: 2,
                minWidth: 240,
                mt: 1.5,
                boxShadow:
                  "0px 1px 2px -1px #0000001A, 0px 1px 3px 0px #0000001A",
              },
            }}
          >
            {/* User Info Section */}
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {user?.fullName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "0.75rem" }}
              >
                {user?.email || "admin@example.com"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "0.75rem", mt: 0.5 }}
              >
                {user[2]?.fullName}
              </Typography>
            </Box>

            <Divider />

            {/* Logout Menu Item */}
            <MenuItem
              onClick={handleLogout}
              sx={{
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
